/**
 * Fullscreen API service with cross-browser support
 */

type FullscreenElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
};

type FullscreenDocument = Document & {
    webkitFullscreenElement?: Element | null;
    msFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
};

/**
 * Check if fullscreen mode is currently active.
 */
export function isFullscreen(): boolean {
    const doc = document as FullscreenDocument;
    return !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.msFullscreenElement
    );
}

/**
 * Request fullscreen mode for the given element (defaults to document.documentElement).
 */
export async function enterFullscreen(element?: HTMLElement): Promise<boolean> {
    const el = (element || document.documentElement) as FullscreenElement;

    try {
        if (el.requestFullscreen) {
            await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
            await el.webkitRequestFullscreen();
        } else if (el.msRequestFullscreen) {
            await el.msRequestFullscreen();
        } else {
            console.warn('[Fullscreen] API not supported');
            return false;
        }
        return true;
    } catch (error) {
        console.error('[Fullscreen] Failed to enter fullscreen:', error);
        return false;
    }
}

/**
 * Exit fullscreen mode.
 */
export async function exitFullscreen(): Promise<boolean> {
    const doc = document as FullscreenDocument;

    try {
        if (doc.exitFullscreen) {
            await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
            await doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) {
            await doc.msExitFullscreen();
        } else {
            console.warn('[Fullscreen] Exit API not supported');
            return false;
        }
        return true;
    } catch (error) {
        console.error('[Fullscreen] Failed to exit fullscreen:', error);
        return false;
    }
}

/**
 * Toggle fullscreen mode.
 */
export async function toggleFullscreen(element?: HTMLElement): Promise<boolean> {
    if (isFullscreen()) {
        return exitFullscreen();
    } else {
        return enterFullscreen(element);
    }
}

/**
 * Add a listener for fullscreen changes.
 */
export function onFullscreenChange(callback: (isFullscreen: boolean) => void): () => void {
    const handler = () => callback(isFullscreen());

    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('msfullscreenchange', handler);

    // Return cleanup function
    return () => {
        document.removeEventListener('fullscreenchange', handler);
        document.removeEventListener('webkitfullscreenchange', handler);
        document.removeEventListener('msfullscreenchange', handler);
    };
}
